import React, { useContext, useEffect, useState } from "react";
import { Grid, IconButton, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

import { UserContext } from "App";

import { useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";

import { GetAlbumDetails } from "APIs/AlbumAPIs";

// Receipt page theme (shared)
import SectionCard, {
  SectionHeader,
  ranuOutlinedButtonSx,
  useAccent,
} from "layouts/receipt/components/SectionCard";

import FormField from "layouts/receipt/components/FormField";
import { Loader } from "components/Custom/Loader";

// Smaller inner padding for the cards, so the content gets more width.
// (only affects the cards inside this wrapper, the shared SectionCard is not changed)
const compactCardsSx = {
  "& > div": {
    padding: { xs: "12px", md: "16px" },
  },
};
const goldHoverSx = ({ palette }) => ({
  "&:hover": {
    backgroundColor: palette.secondary.main,
    borderColor: palette.secondary.main,
    color: palette.white.main,
    boxShadow: "none",
  },
});
// Thumbnail frame
const tileSx = ({ palette, borders }) => ({
  position: "relative",
  display: "block",
  width: "100%",
  height: 160,
  overflow: "hidden",
  textDecoration: "none",
  borderRadius: borders.borderRadius.lg,
  backgroundColor: palette.light.main,
  border: `${borders.borderWidth[1]} solid ${borders.borderColor}`,
});

const ViewAlbum = () => {
  const userData = useContext(UserContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { accentName } = useAccent();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [albumDetails, setAlbumDetails] = useState(null);
  const [isFetchingData, setIsFetchingData] = useState(true);

  const getAlbumDetailsFunc = async (albumId, access_token, dispatchFunc) => {
    setIsFetchingData(true);
    const response = await GetAlbumDetails(albumId, access_token, dispatchFunc);

    if (response !== null) {
      setAlbumDetails(response);
    }
    setIsFetchingData(false);
  };

  useEffect(() => {
    if (userData?.access_token !== undefined) {
      getAlbumDetailsFunc(id, userData.access_token, dispatch);
    }
  }, [userData, id]);

  const mediaCount = albumDetails?.list?.length || 0;

  return (
    <MDBox px={{ xs: 0, md: 1 }} pt={{ xs: 0, md: 1 }} pb={3}>
      <SectionHeader
        icon="visibility"
        label="VIEW ALBUM"
        startAction={
          <MDButton
            variant="outlined"
            color={accentName}
            size="medium"
            sx={ranuOutlinedButtonSx}
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
          >
            Back
          </MDButton>
        }
        endAction={
          !isMobile && (
            <MDButton
              color="primary"
              size="medium"
              sx={[ranuOutlinedButtonSx, goldHoverSx]}
              startIcon={<EditIcon />}
              onClick={() => navigate(`/album/edit/${id}`)}
            >
              Edit Album
            </MDButton>
          )
        }
      />
      {isFetchingData ? (
        <Loader />
      ) : (
        <>
          <MDBox sx={compactCardsSx}>
            {/* ------------------------------ Details ------------------------------ */}
            <SectionCard title="Details">
              <Grid container spacing={2}>
                <FormField name="title" full>
                  <MDInput disabled fullWidth value={albumDetails?.media_album_title || ""} />
                </FormField>
              </Grid>
            </SectionCard>

            {/* ------------------------------- Media ------------------------------- */}
            <SectionCard
              title={`Media (${mediaCount})`}
              subtitle="Click the icon on any image to open it in a new tab"
            >
              <Grid container spacing={2}>
                {albumDetails?.list && albumDetails.list.length > 0 ? (
                  albumDetails.list.map((item, index) => (
                    <Grid item xs={6} sm={4} md={3} key={item.media_id || index}>
                      <MDBox sx={tileSx}>
                        <img
                          src={item.media_location}
                          alt={`media_${index}`}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            display: "block",
                          }}
                        />

                        <Tooltip title="Open in new tab">
                          <IconButton
                            size="small"
                            component="a"
                            href={item.media_location}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              position: "absolute",
                              top: 6,
                              right: 6,
                              backgroundColor: "rgba(0, 0, 0, 0.65)",
                              color: "#ffffff",
                              padding: "4px",
                            }}
                          >
                            <OpenInNewIcon style={{ fontSize: "16px" }} />
                          </IconButton>
                        </Tooltip>
                      </MDBox>
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <MDTypography variant="button" color="text">
                      No media available in this album.
                    </MDTypography>
                  </Grid>
                )}
              </Grid>
            </SectionCard>
          </MDBox>

          {/* Mobile Edit Button */}
          {isMobile && (
            <MDBox
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 2,
              }}
            >
              <MDButton
                color="primary"
                size="medium"
                sx={[ranuOutlinedButtonSx, goldHoverSx]}
                startIcon={<EditIcon />}
                onClick={() => navigate(`/blog/edit/${id}`)}
              >
                Edit Album
              </MDButton>
            </MDBox>
          )}
        </>
      )}
    </MDBox>
  );
};

export default ViewAlbum;
